/**
 * Broadcast elements as room metadata carries them (docs/features/badger-cohost.md, Broadcast elements): one small spec
 * per slot on each stage, `stages[<id>].graphics`. A spec names what to show, never the data: every viewer fetches the
 * athlete or the standings from public endpoints and keeps them fresh. Reading is defensive, as the rest of the
 * metadata is: an unknown kind, a spec in the wrong slot or one missing what it names is left out, never an error.
 */

/** Where an element sits over the canvas: the lower third (bottom centre) or the panel (on the right). */
export type GraphicSlot = 'lower_third' | 'panel'

export const GRAPHIC_SLOTS: readonly GraphicSlot[] = ['lower_third', 'panel']

/** A leaderboard's gender filter, as the roster's `gender`. */
export type RaceGender = 'female' | 'male'

/** An athlete's card in the lower third. It leaves `holdS` seconds after `shownAt`. */
export interface AthleteGraphic {
  kind: 'athlete'
  athleteId: string
  /** Seconds it stays up (the API's default is 30). */
  holdS: number
  /**
   * When the host showed it, as the server wrote it (ISO, milliseconds): the spec's identity too, so the same card
   * republished with the rest of the metadata keeps its time on screen. Null when the metadata leaves it out.
   */
  shownAt: string | null
}

/** A leaderboard in the panel: a race, optionally one gender or a hand-picked few. It stays until cleared or replaced. */
export interface StandingsGraphic {
  kind: 'standings'
  routeId: string
  gender: RaceGender | null
  /** How many rows (3–10); null leaves it to the element. */
  limit: number | null
  /** A custom board ("Toni, Jim and 434"); null for the whole race. */
  athleteIds: string[] | null
  /** The host's or Badger's short title; null derives one from the race and gender. */
  title: string | null
  shownAt: string | null
}

export type LowerThirdGraphic = AthleteGraphic
export type PanelGraphic = StandingsGraphic
export type StageGraphic = LowerThirdGraphic | PanelGraphic
export type GraphicKind = StageGraphic['kind']

/** What a stage shows over its canvas, by slot. Absent: nothing there. */
export interface StageGraphics {
  lowerThird?: LowerThirdGraphic | null
  panel?: PanelGraphic | null
}

export const NO_GRAPHICS: StageGraphics = Object.freeze({})

/** Each slot's key on `StageGraphics`. */
export const SLOT_KEY = { lower_third: 'lowerThird', panel: 'panel' } as const satisfies Record<GraphicSlot, keyof StageGraphics>

/** The most rows a panel asks for, and the fewest (`StageGraphic::LIMITS` in the API). */
export const PANEL_LIMITS = { min: 3, max: 10 } as const
/** A lower third's default and longest hold (`StageGraphic::HOLD_S`, `MAX_HOLD_S`). */
export const HOLD_S = 30
export const MAX_HOLD_S = 120
/** A title's longest (`StageGraphic::TITLE_LENGTH`). */
export const TITLE_LENGTH = 40
/** The most athletes a custom board names (`StageGraphic::MAX_ATHLETES`). */
export const MAX_BOARD_ATHLETES = 20

type Raw = Record<string, unknown>

const id = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null)
const shownAtOf = (value: unknown): string | null => (typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null)
const integer = (value: unknown): number | null => (typeof value === 'number' && Number.isInteger(value) ? value : null)

/**
 * Each kind's slot and reader: a new element is one entry here, one component and one registry entry
 * (`components/watch/broadcast/registry.tsx`).
 */
const KINDS: { [K in GraphicKind]: { slot: GraphicSlot; read(raw: Raw): Extract<StageGraphic, { kind: K }> | null } } = {
  athlete: {
    slot: 'lower_third',
    read(raw) {
      const athleteId = id(raw.athlete_id)
      if (!athleteId) return null
      const hold = integer(raw.hold_s)
      return { kind: 'athlete', athleteId, holdS: hold !== null && hold >= 1 ? Math.min(hold, MAX_HOLD_S) : HOLD_S, shownAt: shownAtOf(raw.shown_at) }
    },
  },
  standings: {
    slot: 'panel',
    read(raw) {
      const routeId = id(raw.route_id)
      if (!routeId) return null
      const limit = integer(raw.limit)
      const athleteIds = Array.isArray(raw.athlete_ids) ? raw.athlete_ids.map(id).filter((each): each is string => each !== null) : []
      const title = typeof raw.title === 'string' ? raw.title.trim().slice(0, TITLE_LENGTH) : ''
      return {
        kind: 'standings',
        routeId,
        gender: raw.gender === 'female' || raw.gender === 'male' ? raw.gender : null,
        limit: limit === null ? null : Math.min(Math.max(limit, PANEL_LIMITS.min), PANEL_LIMITS.max),
        athleteIds: athleteIds.length ? athleteIds.slice(0, MAX_BOARD_ATHLETES) : null,
        title: title || null,
        shownAt: shownAtOf(raw.shown_at),
      }
    },
  },
}

const isKind = (kind: unknown): kind is GraphicKind => typeof kind === 'string' && Object.prototype.hasOwnProperty.call(KINDS, kind)

/** The slot a kind of element goes in. */
export function slotOf(kind: GraphicKind): GraphicSlot {
  return KINDS[kind].slot
}

/**
 * A stage's `graphics` from room metadata: `{lower_third?: {...}, panel?: {...}}`. Missing, `{}`, or anything that
 * isn't an object reads as nothing on screen; an unknown kind, a kind in another kind's slot, or a spec missing its
 * athlete or race is left out.
 */
export function parseStageGraphics(raw: unknown): StageGraphics {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return NO_GRAPHICS
  const graphics: StageGraphics = {}
  for (const slot of GRAPHIC_SLOTS) {
    const spec = (raw as Raw)[slot]
    if (!spec || typeof spec !== 'object' || Array.isArray(spec)) continue
    const kind = (spec as Raw).kind
    if (!isKind(kind) || KINDS[kind].slot !== slot) continue
    const read = KINDS[kind].read(spec as Raw)
    if (read) (graphics as Record<string, StageGraphic>)[SLOT_KEY[slot]] = read
  }
  return Object.keys(graphics).length ? graphics : NO_GRAPHICS
}

/** The same graphic: what a slot shows didn't change (a republish of the rest of the metadata). */
export function sameGraphic(a: StageGraphic | null | undefined, b: StageGraphic | null | undefined): boolean {
  if (!a || !b) return !a && !b
  return JSON.stringify(a) === JSON.stringify(b)
}

/** A graphic as the API's PUT takes it (`{graphic: {...}}`'s inside). */
export function graphicBody(graphic: StageGraphicInput): Record<string, unknown> {
  if (graphic.kind === 'athlete') {
    return { kind: 'athlete', athlete_id: graphic.athleteId, ...(graphic.holdS ? { hold_s: graphic.holdS } : {}) }
  }
  return {
    kind: 'standings',
    route_id: graphic.routeId,
    ...(graphic.gender ? { gender: graphic.gender } : {}),
    ...(graphic.limit ? { limit: graphic.limit } : {}),
    ...(graphic.athleteIds?.length ? { athlete_ids: graphic.athleteIds } : {}),
    ...(graphic.title ? { title: graphic.title } : {}),
  }
}

/** What the console and Badger ask a slot to show: a spec without the server's time. */
export type StageGraphicInput =
  | { kind: 'athlete'; athleteId: string; holdS?: number | null }
  | { kind: 'standings'; routeId: string; gender?: RaceGender | null; limit?: number | null; athleteIds?: string[] | null; title?: string | null }

/** The input as a spec the page can draw now (the host's preview), its time unknown. */
export function previewGraphic(input: StageGraphicInput): StageGraphic {
  if (input.kind === 'athlete') return { kind: 'athlete', athleteId: input.athleteId, holdS: input.holdS ?? HOLD_S, shownAt: null }
  return {
    kind: 'standings',
    routeId: input.routeId,
    gender: input.gender ?? null,
    limit: input.limit ?? null,
    athleteIds: input.athleteIds?.length ? input.athleteIds : null,
    title: input.title ?? null,
    shownAt: null,
  }
}
